import { useCallback, useMemo } from 'react'
import { useParams } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSetAtom } from 'jotai'
import type {
  GuidedChapterDTO,
  GuidedPathSnapshotDTO,
  GuidedStepDTO,
} from '@deepguide-ai/types'
import { trpc } from '@/lib/trpc'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import {
  chatProgrammaticMessageAtom,
  screenataAssistantOpenAtom,
} from '@/stores/screenataPageContextStore'

/**
 * RFC-231 — the one client-side reader of `complianceProgram.getGuidedPath`.
 *
 * The Setup guide page, the sidebar percentage and the ChatWelcome hero all
 * consume this hook, so they can never disagree about what's next (§5 Rule 2).
 * The server owns every `done`; this hook only filters (feature flag, skips)
 * and counts what it will actually render — counts stay client-side because
 * the Trust Center step is flag-gated and a server total would strand the
 * guide one short of complete.
 */

export type GuidedStep = GuidedStepDTO & {
  /** Educational guidance was dismissed; operational coverage remains applicable. */
  skipped: boolean
}

export type GuidedChapter = Omit<GuidedChapterDTO, 'steps'> & {
  steps: GuidedStep[]
  done: number
  total: number
  /**
   * Nothing left to do. A chapter whose every step was skipped or flag-gated
   * away counts as complete — 0/0 is finished work, not pending work.
   */
  complete: boolean
}

export interface GuidedPathView {
  workspaceId: string | undefined
  snapshot: GuidedPathSnapshotDTO | undefined
  /** Every query the surfaces gate on has settled (success or error). */
  isReady: boolean
  isError: boolean
  retry: () => void
  /** Admin/Owner on a workspace with a program — the only audience for the guide. */
  eligible: boolean
  chapters: GuidedChapter[]
  skippedSteps: GuidedStep[]
  completedCount: number
  totalCount: number
  pct: number
  complete: boolean
  /** First non-auto, not-done, not-skipped step in chapter order. */
  nextStep: { chapter: GuidedChapter; step: GuidedStep } | null
  askVera: (step: GuidedStep) => void
  /** Send an arbitrary prompt (a single unanswered onboarding question) to Vera. */
  askVeraPrompt: (text: string, stepKey: string) => void
  setStepSkipped: (key: string, skipped: boolean) => void
  isSkipping: boolean
}

export function useGuidedPath(): GuidedPathView {
  const { workspaceId } = useParams({ strict: false }) as {
    workspaceId?: string
  }
  const { isAdmin, isLoading: permsLoading } = useWorkspacePermissions(workspaceId)
  const eligible = Boolean(workspaceId) && !permsLoading && isAdmin
  const queryOptions = trpc.complianceProgram.getGuidedPath.queryOptions()
  const query = useQuery({
    ...queryOptions,
    queryKey: [...queryOptions.queryKey, { workspaceId }],
    enabled: eligible,
    // Poll startup quickly, then refresh the bounded server snapshot for progress changes.
    refetchInterval: (q) => {
      const phase = (q.state.data as GuidedPathSnapshotDTO | undefined)?.setup.phase
      if (!phase) return false
      return phase !== 'done' && phase !== 'failed' ? 3000 : 15_000
    },
    // A 15-second server snapshot plus this interval targets ~30-second progress freshness.
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  })
  const snapshot = eligible ? query.data : undefined
  // Trust Center is flag-gated in the sidebar (`features: ['trustCenter']`), so
  // its step only counts for workspaces that can actually reach the page.
  const trustCenter = useFeatureFlag('trustCenter')

  const chapters: GuidedChapter[] = useMemo(() => {
    if (!snapshot) return []
    const skipped = new Set(snapshot.skippedSteps.map((s) => s.key))
    return snapshot.chapters.map((chapter) => {
      const steps: GuidedStep[] = chapter.steps
        .filter((step) => step.featureFlag !== 'trustCenter' || trustCenter.enabled)
        .map((step) => ({
          ...step,
          // `!step.done` so a step that was skipped and later completed for
          // real returns as complete rather than staying hidden.
          skipped: skipped.has(step.key) && !step.done,
        }))
      const active = steps.filter((s) => !s.skipped || s.coverage)
      return {
        ...chapter,
        steps,
        done: active.filter((s) => s.done).length,
        total: active.length,
        complete: active.every((s) => s.done),
      }
    })
  }, [snapshot, trustCenter.enabled])

  const skippedSteps = useMemo(
    () => chapters.flatMap((c) => c.steps.filter((s) => s.skipped)),
    [chapters],
  )
  const completedCount = chapters.reduce((n, c) => n + c.done, 0)
  const totalCount = chapters.reduce((n, c) => n + c.total, 0)
  const pct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0
  const complete = totalCount > 0 && completedCount >= totalCount

  const nextStep = useMemo(() => {
    for (const chapter of chapters) {
      const step = chapter.steps.find((s) => !s.done && (!s.skipped || s.coverage) && !s.auto)
      if (step) return { chapter, step }
    }
    return null
  }, [chapters])

  const queryClient = useQueryClient()
  const { mutate, isPending: isSkipping } = useMutation({
    ...trpc.complianceProgram.setGuidedStepSkipped.mutationOptions(),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getGuidedPath.queryKey(),
      })
    },
  })
  const setStepSkipped = useCallback(
    (key: string, skipped: boolean) => mutate({ key: key as never, skipped }),
    [mutate],
  )

  // "Ask Vera" — open the rail and send the step's canned prompt with the step
  // key as hidden context. On chat-main-view routes the composer IS the main
  // panel and picks the programmatic message up directly.
  const setAssistantOpen = useSetAtom(screenataAssistantOpenAtom)
  const setProgrammaticMessage = useSetAtom(chatProgrammaticMessageAtom)
  const askVeraPrompt = useCallback(
    (text: string, stepKey: string) => {
      setAssistantOpen(true)
      setProgrammaticMessage({
        text,
        activityContext: { guideStep: stepKey },
        metadata: { source: 'setup-guide', guideStep: stepKey },
      })
    },
    [setAssistantOpen, setProgrammaticMessage],
  )
  const askVera = useCallback(
    (step: GuidedStep) => {
      if (!step.veraPrompt) return
      askVeraPrompt(step.veraPrompt, step.key)
    },
    [askVeraPrompt],
  )

  const isReady =
    !eligible || query.isSuccess || query.isError
      ? !permsLoading && (!eligible || !trustCenter.loading)
      : false

  return {
    workspaceId,
    snapshot,
    isReady,
    isError: query.isError,
    retry: () => { void query.refetch() },
    eligible,
    chapters,
    skippedSteps,
    completedCount,
    totalCount,
    pct,
    complete,
    nextStep,
    askVera,
    askVeraPrompt,
    setStepSkipped,
    isSkipping,
  }
}
